import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useCart } from '../context/CartContext';
import { usePoints } from '../context/PointsContext';
import { useWishlist } from '../context/WishlistContext';
import { API_URL, getFullImageUrl } from '../services/api'; 
import { useNavigate } from 'react-router-dom';
import HeaderWithSearch from '../components/HeaderWithSearch';
import Toast from '../components/Toast';
import ProductSearch from '../components/ProductSearch';
import './Home.css';

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const FALLBACK_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"%3E%3Crect width="300" height="300" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';

interface VariationSize {
  ukSize: number;
  footLengthCm: number;
  stockQuantity: number;
}

interface Variation {
  color: string;
  colorCode: string;
  images: string[];
  stockQuantity: number;
  additionalPrice: number;
  isDefault?: boolean;
  sizes?: VariationSize[];
}

// UK Shoe Size interface
interface UKShoeSize {
  ukSize: number;
  footLengthCm: number;
  stockQuantity: number;
}

interface Dimension {
  length?: number;
  breadth?: number;
  height?: number;
  unit: 'cm' | 'm' | 'inch';
}

interface AgeRange {
  min: number;
  max: number;
}

interface Product {
  _id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  images?: string[];
  variations?: Variation[];
  category: string;
  inStock: boolean;
  isFeatured?: boolean;
  isPopular?: boolean;
  discount?: number;
  isOverseas?: boolean;
  stockQuantity?: number;
  ukShoeSizes?: UKShoeSize[];
  dimension?: Dimension;
  rimSize?: string;
  ageRange?: AgeRange;
}

const Home: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVariation, setSelectedVariation] = useState<{ [productId: string]: Variation }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [networkError, setNetworkError] = useState<'offline' | 'timeout' | 'error' | null>(null);
  const { addToCart } = useCart();
  const { points, pointsToCurrency } = usePoints();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const navigate = useNavigate();

  // Quick Add Modal States
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [quickAddProduct, setQuickAddProduct] = useState<Product | null>(null);
  const [quickAddQuantity, setQuickAddQuantity] = useState(1);
  const [quickAddSize, setQuickAddSize] = useState<UKShoeSize | null>(null);
  const [quickAddVariation, setQuickAddVariation] = useState<Variation | null>(null);

  const isInitialMount = useRef(true);
// Helper function to truncate product name
  const truncateProductName = (name: string, maxLength: number = 25): string => {
    if (!name) return '';
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength) + '...';
  };

  // Helper function to get shoe sizes from product (from variations or ukShoeSizes)
  const getProductShoeSizes = (product: Product): UKShoeSize[] => {
    // First check if product has ukShoeSizes
    if (product.ukShoeSizes && product.ukShoeSizes.length > 0) {
      return product.ukShoeSizes;
    }
    
    return [];
  };

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setNetworkError(null);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    
    try {
      const response = await fetch(`${API_URL}/products`, {
        signal: controller.signal
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // ✅ Cast the products data to Product[] with proper type checking
      const productsData: Product[] = (data.products || data.data || []) as Product[];
      
      const processedProducts = productsData.map((product: Product) => {
        if (product.variations && product.variations.length > 0) {
          const sortedVariations = [...product.variations].sort((a: Variation, b: Variation) => {
            if (a.isDefault) return -1;
            if (b.isDefault) return 1;
            return 0;
          });
          return { ...product, variations: sortedVariations };
        }
        return product;
      });

      // ✅ SHUFFLE THE PRODUCTS to mix categories
      const shuffledProducts = shuffleArray(processedProducts);

      setProducts(shuffledProducts);
      setFilteredProducts(shuffledProducts);
      
      const initialSelections: { [productId: string]: Variation } = {};
      shuffledProducts.forEach((product: Product) => {
        if (product.variations && product.variations.length > 0) {
          initialSelections[product._id] = product.variations[0];
        }
      });
      setSelectedVariation(initialSelections);
    } catch (err) {
      const error = err as Error;
      console.error('Failed to fetch products:', error);
      
      if (products.length === 0) {
        if (error.name === 'AbortError') {
          setNetworkError('timeout');
        } else if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
          setNetworkError('offline');
        } else {
          setNetworkError('error');
        }
      }
    } finally {
      clearTimeout(timeoutId);
      setIsLoading(false);
    }
  }, [products.length]);

  // Define filterProducts BEFORE using it in useEffect
  const filterProducts = useCallback(() => {
    let filtered = [...products];
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }
    
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query) ||
        p.category.toLowerCase().includes(query)
      );
    }
    
    // ✅ SHUFFLE the filtered results too
    const shuffledFiltered = shuffleArray(filtered);
    setFilteredProducts(shuffledFiltered);
  }, [products, selectedCategory, searchQuery]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchProducts();
    }
  }, [fetchProducts]);

  useEffect(() => {
    filterProducts();
  }, [selectedCategory, products, searchQuery, filterProducts]);

  useEffect(() => {
    const bottomNav = document.querySelector('.bottom-nav') as HTMLElement;
    
    // Function to detect keyboard visibility
    const handleKeyboardVisibility = () => {
      // For Capacitor/Cordova WebView
      if (window.navigator.userAgent.includes('Capacitor')) {
        // Check if any input is focused (keyboard likely open)
        const activeElement = document.activeElement;
        const isInputFocused = activeElement?.tagName === 'INPUT' || 
                              activeElement?.tagName === 'TEXTAREA' ||
                              (activeElement as HTMLElement)?.contentEditable === 'true';
        
        if (bottomNav) {
          if (isInputFocused) {
            // Keyboard is likely open
            bottomNav.style.display = 'none';
            console.log('⌨️ Keyboard likely open - hiding bottom nav');
          } else {
            // Keyboard is likely closed
            bottomNav.style.display = '';
            console.log('⌨️ Keyboard likely closed - showing bottom nav');
          }
        }
      } else {
        // For mobile browsers using visualViewport API
        if (window.visualViewport) {
          const originalHeight = window.innerHeight;
          const currentHeight = window.visualViewport.height;
          const keyboardOpen = currentHeight < originalHeight * 0.85; // 15% reduction indicates keyboard
          
          if (bottomNav) {
            if (keyboardOpen) {
              bottomNav.style.display = 'none';
              console.log('⌨️ Keyboard open - hiding bottom nav');
            } else {
              bottomNav.style.display = '';
              console.log('⌨️ Keyboard closed - showing bottom nav');
            }
          }
        }
      }
    };
    
    // Listen to focus events on all inputs and textareas
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') {
        setTimeout(() => {
          handleKeyboardVisibility();
        }, 100); // Small delay for keyboard to appear
      }
    };
    
    const handleFocusOut = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') {
        setTimeout(() => {
          handleKeyboardVisibility();
        }, 100); // Small delay for keyboard to disappear
      }
    };
    
    // Listen to window resize for visualViewport approach
    const handleResize = () => {
      handleKeyboardVisibility();
    };
    
    // Add event listeners
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    window.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);
    
    // Initial check
    handleKeyboardVisibility();
    
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
      if (bottomNav) {
        bottomNav.style.display = '';
      }
    };
  }, []);

  // Hide bottom nav when modal is open
  useEffect(() => {
    const bottomNav = document.querySelector('.bottom-nav') as HTMLElement;
    
    if (showQuickAddModal) {
      // Hide bottom nav when modal opens
      if (bottomNav) {
        bottomNav.style.display = 'none';
        console.log('🔽 Quick add modal open - hiding bottom nav');
      }
      // Also add class to body to prevent scrolling
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      // Restore bottom nav when modal closes
      if (bottomNav) {
        bottomNav.style.display = '';
        console.log('🔽 Quick add modal closed - showing bottom nav');
      }
      // Restore body scrolling
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    }
    
    // Cleanup function
    return () => {
      if (bottomNav) {
        bottomNav.style.display = '';
      }
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    };
  }, [showQuickAddModal]);


  const clearSearch = () => {
    setSearchQuery('');
  };

  const handleWishlistToggle = (e: React.MouseEvent, product: Product) => {
    e.stopPropagation();
    
    const productId = product._id;
    
    if (isInWishlist(productId)) {
      removeFromWishlist(productId);
      setToast({ 
        message: `Removed from wishlist`, 
        type: 'info' 
      });
    } else {
      addToWishlist({
        _id: productId,
        name: product.name,
        price: product.price,
        image: product.image,
        category: product.category
      });
      setToast({ 
        message: `Added to wishlist`, 
        type: 'success' 
      });
    }
  };

  // Check if product needs configuration (requires opening details or quick modal)
  const needsConfiguration = (product: Product): boolean => {
    // Check for shoe sizes
    const shoeSizes = getProductShoeSizes(product);
    const hasShoeSizes = product.category === 'shoes' && shoeSizes.length > 0;
    
    // Check for color variations
    const hasColorVariations = product.variations && product.variations.length > 0;
    
    // Check for dimension (convert to boolean)
    const hasDimension = product.dimension !== undefined && product.dimension !== null;
    
    // Check for age range (convert to boolean)
    const hasAgeRange = product.ageRange !== undefined && product.ageRange !== null;
    
    return hasShoeSizes || hasColorVariations || hasDimension || hasAgeRange;
  };

  // Open Quick Add Modal
  const openQuickAddModal = (product: Product, e: React.MouseEvent) => {
    e.stopPropagation();
    
    setQuickAddProduct(product);
    setQuickAddQuantity(1);
    setQuickAddSize(null);
    setQuickAddVariation(null);
    
    // Auto-select default variation if exists
    if (product.variations && product.variations.length > 0) {
      const defaultVar = product.variations.find(v => v.isDefault) || product.variations[0];
      setQuickAddVariation(defaultVar);
      
      // Auto-select first in-stock size for this variation (for shoes)
      if (product.category === 'shoes' && defaultVar.sizes && defaultVar.sizes.length > 0) {
        const inStock = defaultVar.sizes.find(s => s.stockQuantity > 0);
        if (inStock) {
          setQuickAddSize({
            ukSize: inStock.ukSize,
            footLengthCm: inStock.footLengthCm,
            stockQuantity: inStock.stockQuantity
          });
        }
      }
    } else if (product.category === 'shoes' && product.ukShoeSizes) {
      // If no variations but has ukShoeSizes
      const inStock = product.ukShoeSizes.find(s => s.stockQuantity > 0);
      if (inStock) setQuickAddSize(inStock);
    }
    
    setShowQuickAddModal(true);
  };


  // Handle add to cart (either directly or via modal)
  const handleAddToCart = (product: Product, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Check if product needs configuration
    if (needsConfiguration(product)) {
      // Products that need configuration - open quick modal
      openQuickAddModal(product, e);
      return;
    }
    
    // Simple products - add directly to cart
    let finalPrice = product.price;
    
    if (product.discount && product.discount > 0) {
      finalPrice = finalPrice * (1 - product.discount / 100);
    }
    
    const cartItemId = product._id;
    
    addToCart({
      cartItemId: cartItemId,
      id: cartItemId,
      productId: product._id,
      name: product.name,
      price: finalPrice,
      image: getFullImageUrl(product.image),
      quantity: 1,
      category: product.category,
      originalPrice: product.price,
      discount: product.discount,
      isOverseas: product.isOverseas || false
    });
    
    alert(`Added ${product.name} to cart!`);
  };

  // Handle quick add to cart from modal
  const handleQuickAddToCart = () => {
    if (!quickAddProduct) return;
    
    // Validate size selection for shoes
    if (quickAddProduct.category === 'shoes') {
      if (!quickAddSize) {
        alert('Please select a shoe size');
        return;
      }
      
      // Check if the selected size is in stock for the selected variation
      let isInStock = false;
      let stockQuantity = 0;
      
      if (quickAddVariation && quickAddVariation.sizes) {
        const sizeData = quickAddVariation.sizes.find(s => s.ukSize === quickAddSize?.ukSize);
        if (sizeData) {
          isInStock = sizeData.stockQuantity > 0;
          stockQuantity = sizeData.stockQuantity;
        }
      } else if (quickAddProduct.ukShoeSizes) {
        const sizeData = quickAddProduct.ukShoeSizes.find(s => s.ukSize === quickAddSize?.ukSize);
        if (sizeData) {
          isInStock = sizeData.stockQuantity > 0;
          stockQuantity = sizeData.stockQuantity;
        }
      }
      
      if (!isInStock) {
        alert('This size is out of stock');
        return;
      }
      if (quickAddQuantity > stockQuantity) {
        alert(`Only ${stockQuantity} units available for this size`);
        return;
      }
    }
    
    // Validate variation selection
    if (quickAddVariation && quickAddVariation.stockQuantity === 0 && !quickAddVariation.sizes) {
      alert('This color is out of stock');
      return;
    }
    
    // Calculate final price
    let finalPrice = quickAddProduct.price;
    if (quickAddVariation?.additionalPrice) {
      finalPrice += quickAddVariation.additionalPrice;
    }
    if (quickAddProduct.discount && quickAddProduct.discount > 0) {
      finalPrice = finalPrice * (1 - quickAddProduct.discount / 100);
    }
    
    // Build display name
    let displayName = quickAddProduct.name;
    if (quickAddVariation) {
      displayName += ` (${quickAddVariation.color})`;
    }
    if (quickAddSize && quickAddProduct.category === 'shoes') {
      displayName += ` - UK ${quickAddSize.ukSize} (${quickAddSize.footLengthCm}cm)`;
    }
    
    // Generate unique ID for cart item
    const cartItemId = `${quickAddProduct._id}_${quickAddVariation?.color || ''}_${quickAddSize?.ukSize || ''}`;
    
    // Get image URL
    let imageUrl = quickAddProduct.image;
    if (quickAddVariation?.images && quickAddVariation.images.length > 0) {
      imageUrl = quickAddVariation.images[0];
    }
    
    addToCart({
      cartItemId: cartItemId,
      id: cartItemId,
      productId: quickAddProduct._id,
      name: displayName,
      price: finalPrice,
      image: getFullImageUrl(imageUrl),
      quantity: quickAddQuantity,
      category: quickAddProduct.category,
      originalPrice: quickAddProduct.price,
      discount: quickAddProduct.discount,
      isOverseas: quickAddProduct.isOverseas || false,
      selectedColor: quickAddVariation?.color,
      selectedSize: quickAddSize ? `UK ${quickAddSize.ukSize}` : undefined,
      selectedSizeCm: quickAddSize?.footLengthCm,
      variationId: quickAddVariation?.color
    });
    
    alert(`Added ${quickAddQuantity} × ${displayName} to cart!`);
    setShowQuickAddModal(false);
  };

  // Function to get available sizes for the selected variation
  const getAvailableSizesForVariation = (product: Product, variation: Variation | null): UKShoeSize[] => {
    if (!product || product.category !== 'shoes') return [];
    
    if (variation && variation.sizes && variation.sizes.length > 0) {
      return variation.sizes.map(size => ({
        ukSize: size.ukSize,
        footLengthCm: size.footLengthCm,
        stockQuantity: size.stockQuantity
      }));
    }
    
    return getProductShoeSizes(product);
  };

  const getProductDisplayImage = (product: Product) => {
    const selected = selectedVariation[product._id];
    if (selected && selected.images && selected.images[0]) {
      return getFullImageUrl(selected.images[0]);
    }
    return getFullImageUrl(product.image);
  };

  
  
    

    const getProductPrice = (product: Product) => {
      const selected = selectedVariation[product._id];
      let basePrice = product.price;
      
      if (selected?.additionalPrice) {
        basePrice += selected.additionalPrice;
      }
      
      if (product.discount && product.discount > 0) {
        const discountedPrice = basePrice * (1 - product.discount / 100);
        return (
          <>
            <span className="original-price">₦{basePrice.toLocaleString()}</span>
            <span className="sale-price">₦{discountedPrice.toLocaleString()}</span>
          </>
        );
      }
      
      return <span className="price">₦{basePrice.toLocaleString()}</span>;
    };

    // Get the current category name
    const getCurrentCategoryName = () => {
      const category = categories.find(cat => cat.id === selectedCategory);
      return category ? category.name : 'category';
    };

    const categories = [
      { id: 'all', name: 'All', icon: '🛍️' },
      { id: 'shoes', name: 'Shoes', icon: '👟' },
      { id: 'bags', name: 'Bags', icon: '👜' },
      { id: 'handbags', name: 'Handbags', icon: '👛' },
      { id: 'travel', name: 'Travel', icon: '🧳' },
      { id: 'home', name: 'Home', icon: '🏠' },
      { id: 'kitchen', name: 'Kitchen', icon: '🍳' },
      { id: 'school', name: 'School', icon: '📚' },
      { id: 'clothing', name: 'Clothing', icon: '👕' },
      { id: 'accessories', name: 'Accessories', icon: '💍' },
      { id: 'wellness', name: 'Wellness', icon: '🧘' }
    ];

    return (
      <div className="home-page">
        {/* Add HeaderWithSearch at the top */}
        <HeaderWithSearch />
        <ProductSearch />

        <section className="home-hero" aria-labelledby="home-hero-title">
          <div className="home-hero-content">
            <span className="home-hero-eyebrow">GIMBIYA VALUE STORE</span>
            <h1 id="home-hero-title">Discover everyday finds at Gimbiya</h1>
            <p>
              Explore shoes, bags, clothing, home essentials, accessories and more.
            </p>
            <button
              type="button"
              className="home-hero-cta"
              onClick={() => navigate('/products')}
            >
              Shop Products <span aria-hidden="true">→</span>
            </button>
          </div>
          <div className="home-hero-mark" aria-hidden="true">GVS</div>
        </section>

        <div className="home-content">
          {/* Rewards Banner */}
          <div className="rewards-banner">
            <div className="rewards-info">
              <span className="rewards-icon">
                <img src="/money_bag_1_trans.png" alt="Naira Bag"/></span>
              <span className="rewards-text">Reward Points</span>
              <span className="rewards-points">{points} pts</span>
              <span className="rewards-value">(₦{pointsToCurrency(points).toLocaleString()} value)</span>
            </div>
          </div>

          {/* Category Filter */}
          <div className="category-filter">
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`category-crap ${selectedCategory === cat.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat.id)}
              >
                <span>{cat.icon}</span>
                <span>{cat.name}</span>
              </button>
            ))}
          </div>

          <div className="view-all-container">
            <button 
              className="view-all-btn" 
              onClick={() => navigate('/products')}
            >
              View All Products
            </button>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="loading-products">
              <div className="loading-spinner-home">
                <div className="spinner"></div>
              </div>
            </div>
          )}
          {/* Quiet retry action when products cannot be loaded */}
          {!isLoading && networkError && products.length === 0 && (
            <div className="network-retry">
              <button className="retry-button" onClick={fetchProducts}>
                <svg className="retry-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.65 6.35A8 8 0 1 0 19 12h-2a6 6 0 1 1-1.77-4.23L17 10h-5v2h7V5h-2v1.35z" fill="currentColor"/>
                </svg>
                Try Again
              </button>
            </div>
          )}

          {/* Products Grid - Only show when not loading AND has products */}
          {!isLoading && filteredProducts.length > 0 && (
            <div className="products-grid">
              {filteredProducts.map((product) => {
                const inWishlist = isInWishlist(product._id);
                
                return (
                  <div key={product._id} className="product-card" onClick={() => navigate(`/product/${product._id}`)}>
                    <div className="product-image-container">
                      {product.isOverseas && (
                        <span className="overseas-badge">✈️ Overseas</span>
                      )}
                      
                      {/* Wishlist Button */}
                      <button 
                        className={`wishlist-btn ${inWishlist ? 'active' : ''}`}
                        onClick={(e) => handleWishlistToggle(e, product)}
                        title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
                      >
                        {inWishlist ? '❤️' : '🤍'}
                      </button>
                      
                      <img 
                        src={getProductDisplayImage(product)} 
                        alt={product.name} 
                        className="product-image"
                        onError={(e) => {
                          const image = e.currentTarget;

                          if (image.dataset.fallbackApplied === 'true') {
                            return;
                          }

                          image.dataset.fallbackApplied = 'true';
                          image.src = FALLBACK_IMAGE;
                        }}
                      />
                      {(product.discount && product.discount > 0) ? (
                        <span className="discount-badge">-{product.discount}%</span>
                      ) : null}
                    </div>
                    <div className="product-details">
                      <h3 className="product-name" title={product.name}>
                        {truncateProductName(product.name, 25)}
                      </h3>
                      
                
                      
                      <div className="product-price-row">
                        {getProductPrice(product)}
                      </div>
                      
                      {/* Add to Cart Button */}
                      <button
                        className="add-to-cart-btn"
                        onClick={(e) => handleAddToCart(product, e)}
                        disabled={!product.inStock}
                      >
                        {product.inStock ? 'Add to Cart 🛒' : ' Sold Out'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty State - Only show when NOT loading, no network error, and no products */}
          {!isLoading && !networkError && products.length === 0 && (
            <div className="no-products">
              <div className="empty-state-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 6h-4V4c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM10 4h4v2h-4V4zm10 16H4V8h16v12z" fill="currentColor"/>
                </svg>
              </div>
              <p>No Products Yet</p>
              <p className="coming-soon-subtitle">
                The store is being set up. Check back soon for amazing products!
              </p>
            </div>
          )}

          {/* Category Empty State - Show when products exist but selected category has none */}
          {!isLoading && !networkError && products.length > 0 && filteredProducts.length === 0 && !searchQuery && selectedCategory !== 'all' && (
            <div className="no-products">
              <div className="empty-state-icon coming-soon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
                  <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" stroke="currentColor" strokeWidth="0.5" fill="none"/>
                </svg>
              </div>
              <p className="coming-soon-title">Coming Soon! ✨</p>
              <p className="coming-soon-subtitle">
                We're working on adding exciting products to the {getCurrentCategoryName()} category.
                Check back later for new arrivals!
              </p>
              <button 
                className="clear-search-link" 
                onClick={() => setSelectedCategory('all')}
              >
                Browse All Categories
              </button>
            </div>
          )}

          {/* Search Empty State - Show when search returns no results */}
          {!isLoading && searchQuery && filteredProducts.length === 0 && (
            <div className="no-products">
              <div className="empty-state-icon search-empty">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
                </svg>
              </div>
              <p>No products found for "{searchQuery}"</p>
              <button className="clear-search-link" onClick={clearSearch}>
                Clear Search
              </button>
            </div>
          )}

          {/* Quick Add Modal */}
          {showQuickAddModal && quickAddProduct && (
            <div 
              className="home-modal-overlay" 
              onClick={() => {
                setShowQuickAddModal(false);
                // Force bottom nav to show after modal closes
                setTimeout(() => {
                  const bottomNav = document.querySelector('.bottom-nav') as HTMLElement;
                  if (bottomNav && !document.querySelector('.home-modal-overlay')) {
                    bottomNav.style.display = '';
                  }
                }, 100);
              }}
            >
              <div className="quick-add-modal" onClick={e => e.stopPropagation()}>
                <button 
                  className="modal-close-btn"
                  onClick={() => {
                    setShowQuickAddModal(false);
                    // Restore bottom nav
                    setTimeout(() => {
                      const bottomNav = document.querySelector('.bottom-nav') as HTMLElement;
                      if (bottomNav) {
                        bottomNav.style.display = '';
                      }
                    }, 100);
                  }}
                >
                  ×
                </button>
                
                <h3>Add {quickAddProduct.name} to Cart</h3>
                
                {/* Product Image Preview */}
                <div className="quick-add-product-image">
                  <img 
                    src={getFullImageUrl(
                      quickAddVariation?.images?.[0] || quickAddProduct.image
                    )} 
                    alt={quickAddProduct.name}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
                    }}
                  />
                </div>
                
                {/* Size selection for shoes */}
                {quickAddProduct.category === 'shoes' && (() => {
                  const shoeSizes = getAvailableSizesForVariation(quickAddProduct, quickAddVariation);
                  if (shoeSizes.length === 0) return null;
                  
                  return (
                    <div className="form-group">
                      <label>Select UK Size:</label>
                      <div className="size-craps">
                        {shoeSizes.map(size => {
                          const isSelected = quickAddSize?.ukSize === size.ukSize;
                          const isDisabled = size.stockQuantity === 0;
                          return (
                            <button
                              key={size.ukSize}
                              className={`size-crap ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                              onClick={() => {
                                if (!isDisabled) {
                                  setQuickAddSize(size);
                                  setQuickAddQuantity(1); // Reset quantity when size changes
                                }
                              }}
                              disabled={isDisabled}
                              title={isDisabled ? 'Out of stock' : `${size.stockQuantity} available`}
                            >
                              {size.ukSize}
                              {!isDisabled && size.stockQuantity < 10 && (
                                <span className="size-stock-low"> ({size.stockQuantity})</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      {quickAddSize && (
                        <div className="selected-size-info">
                          <span>Foot length: {quickAddSize.footLengthCm}cm</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
                
                {/* Color variation selection */}
                {quickAddProduct.variations && quickAddProduct.variations.length > 0 && (
                  <div className="form-group">
                    <label>Select Color:</label>
                    <div className="color-options-modal">
                      {quickAddProduct.variations.map(variation => {
                        const isSelected = quickAddVariation?.color === variation.color;
                        const hasSizes = variation.sizes && variation.sizes.length > 0;
                        let totalStock = 0;
                        
                        if (hasSizes) {
                          totalStock = variation.sizes!.reduce((sum, s) => sum + s.stockQuantity, 0);
                        } else {
                          totalStock = variation.stockQuantity || 0;
                        }
                        
                        const isDisabled = totalStock === 0;
                        
                        return (
                          <button
                            key={variation.color}
                            className={`color-option-modal ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                            onClick={() => {
                              if (!isDisabled) {
                                setQuickAddVariation(variation);
                                setQuickAddSize(null); // Reset size when color changes
                                setQuickAddQuantity(1);
                                
                                // Auto-select first in-stock size for this variation
                                if (quickAddProduct.category === 'shoes' && variation.sizes && variation.sizes.length > 0) {
                                  const inStock = variation.sizes.find(s => s.stockQuantity > 0);
                                  if (inStock) {
                                    setQuickAddSize({
                                      ukSize: inStock.ukSize,
                                      footLengthCm: inStock.footLengthCm,
                                      stockQuantity: inStock.stockQuantity
                                    });
                                  }
                                }
                              }
                            }}
                            disabled={isDisabled}
                          >
                            <span 
                              className="color-swatch-modal" 
                              style={{ backgroundColor: variation.colorCode }}
                            />
                            <span className="color-name">{variation.color}</span>
                            {variation.additionalPrice > 0 && (
                              <span className="color-price">+₦{variation.additionalPrice.toLocaleString()}</span>
                            )}
                            {hasSizes && (
                              <span className="color-sizes-count">{variation.sizes!.length} sizes</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Dimension display */}
                {quickAddProduct.dimension && (
                  <div className="product-info-display">
                    <div className="realistic-ruler"></div>
                    <span className="info-text">
                      Size: 
                      {quickAddProduct.dimension.length && ` Length: ${quickAddProduct.dimension.length}${quickAddProduct.dimension.unit}`}
                      {quickAddProduct.dimension.breadth && `, Width: ${quickAddProduct.dimension.breadth}${quickAddProduct.dimension.unit}`}
                      {quickAddProduct.dimension.height && `, Height: ${quickAddProduct.dimension.height}${quickAddProduct.dimension.unit}`}
                    </span>
                  </div>
                )}
                
                {/* Rim size for travel */}
                {quickAddProduct.category === 'travel' && quickAddProduct.rimSize && (
                  <div className="product-info-display">
                    <span className="info-icon">🚗</span>
                    <span className="info-text">Rim Size: {quickAddProduct.rimSize}</span>
                  </div>
                )}
                
                {/* Age range info */}
                {quickAddProduct.ageRange && (
                  <div className="product-info-display">
                    <span className="info-icon">🎂</span>
                    <span className="info-text">
                      Recommended Age: {quickAddProduct.ageRange.min}-{quickAddProduct.ageRange.max} years
                    </span>
                  </div>
                )}
                
                {/* Quantity selector */}
                <div className="form-group">
                  <label>Quantity:</label>
                  <div className="quantity-controls-modal">
                    <button 
                      onClick={() => setQuickAddQuantity(Math.max(1, quickAddQuantity - 1))}
                      className="qty-btn"
                    >
                      -
                    </button>
                    <span className="qty-value">{quickAddQuantity}</span>
                    <button 
                      onClick={() => {
                        let maxStock = quickAddProduct.stockQuantity || 0;
                        if (quickAddSize) maxStock = quickAddSize.stockQuantity;
                        if (quickAddVariation) maxStock = quickAddVariation.stockQuantity;
                        if (quickAddQuantity < maxStock) {
                          setQuickAddQuantity(quickAddQuantity + 1);
                        } else {
                          alert(`Only ${maxStock} units available`);
                        }
                      }}
                      className="qty-btn"
                    >
                      +
                    </button>
                  </div>
                  <span className="stock-info">
                    {(() => {
                      let stock = 0;
                      if (quickAddProduct.category === 'shoes') {
                        if (quickAddSize) {
                          stock = quickAddSize.stockQuantity;
                        } else {
                          const sizes = getProductShoeSizes(quickAddProduct);
                          stock = sizes.reduce((sum, s) => sum + s.stockQuantity, 0);
                        }
                      } else if (quickAddVariation) {
                        stock = quickAddVariation.stockQuantity;
                      } else {
                        stock = quickAddProduct.stockQuantity || 0;
                      }
                      return `${stock} items available`;
                    })()}
                  </span>
                </div>
                
                {/* Price summary */}
                <div className="price-summary">
                  <div className="price-row">
                    <span>Unit Price:</span>
                    <span>₦{(() => {
                      let price = quickAddProduct.price;
                      if (quickAddVariation?.additionalPrice) price += quickAddVariation.additionalPrice;
                      if (quickAddProduct.discount && quickAddProduct.discount > 0) {
                        price = price * (1 - quickAddProduct.discount / 100);
                      }
                      return price.toLocaleString();
                    })()}</span>
                  </div>
                  <div className="price-row total">
                    <span>Total:</span>
                    <span>₦{(() => {
                      let price = quickAddProduct.price;
                      if (quickAddVariation?.additionalPrice) price += quickAddVariation.additionalPrice;
                      if (quickAddProduct.discount && quickAddProduct.discount > 0) {
                        price = price * (1 - quickAddProduct.discount / 100);
                      }
                      return (price * quickAddQuantity).toLocaleString();
                    })()}</span>
                  </div>
                </div>
                
                <div className="modal-buttons">
                  <button 
                    className="add-to-cart-btn-modal"
                    onClick={handleQuickAddToCart}
                  >
                    Add to Cart
                  </button>
                  <button 
                    className="view-details-btn-modal"
                    onClick={() => {
                      setShowQuickAddModal(false);
                      // Restore bottom nav before navigation
                      const bottomNav = document.querySelector('.bottom-nav') as HTMLElement;
                      if (bottomNav) {
                        bottomNav.style.display = '';
                      }
                      navigate(`/product/${quickAddProduct._id}`);
                    }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    );
  };

export default Home;